import React, { useState, useEffect } from 'react';
import { fetchCategories, fetchMiddleCategories } from '../services/api';
import { toast } from 'react-toastify';

const SubcategoryFilter = ({ onFilterChange, initialFilters = {} }) => {
  const [categoryId, setCategoryId] = useState(initialFilters.categoryId || '');
  const [middleCategoryId, setMiddleCategoryId] = useState(initialFilters.middleCategoryId || '');
  const [categories, setCategories] = useState([]);
  const [middleCategories, setMiddleCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMiddleCategories, setIsLoadingMiddleCategories] = useState(false);

  // Fetch categories on component mount
  useEffect(() => {
    const loadCategories = async () => {
      setIsLoading(true);
      try {
        const data = await fetchCategories();
        setCategories(data);
        
        // If no initial filter is set, default to Arts
        if (!initialFilters.categoryId) {
          console.log('=== SETTING DEFAULT ARTS CATEGORY ===');
          const artsCategory = data.find(cat => cat.name === 'Arts');
          console.log('Found Arts category:', artsCategory);
          if (artsCategory) {
            console.log('Setting categoryId to:', artsCategory.id);
            setCategoryId(artsCategory.id.toString());
            const defaultFilter = { categoryId: artsCategory.id };
            console.log('Calling onFilterChange with:', defaultFilter);
            onFilterChange(defaultFilter);
          }
        }
      } catch (error) {
        toast.error('Failed to load categories');
        console.error('Error loading categories:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCategories();
  }, []);

  // Fetch middle categories when category changes
  useEffect(() => {
    const loadMiddleCategories = async () => {
      if (!categoryId) {
        setMiddleCategories([]);
        setMiddleCategoryId('');
        return;
      }

      // Find the category name by categoryId
      const selectedCategory = categories.find(cat => cat.id.toString() === categoryId.toString());
      if (!selectedCategory) return;

      setIsLoadingMiddleCategories(true);
      try {
        const data = await fetchMiddleCategories(selectedCategory.name);
        setMiddleCategories(data || []);
        // Reset middle category selection when category changes
        setMiddleCategoryId('');
      } catch (error) {
        toast.error('Failed to load middle categories');
        console.error('Error loading middle categories:', error);
        setMiddleCategories([]);
      } finally {
        setIsLoadingMiddleCategories(false);
      }
    };

    if (categories.length > 0) {
      loadMiddleCategories();
    }
  }, [categoryId, categories]);

  const handleApplyFilters = () => {
    console.log('=== APPLYING FILTERS ===');
    console.log('categoryId:', categoryId);
    console.log('middleCategoryId:', middleCategoryId);
    
    const filterData = {
      categoryId: categoryId || undefined,
      middleCategoryId: middleCategoryId || undefined
    };
    
    console.log('Filter data being sent:', filterData);
    onFilterChange(filterData);
  };

  const handleResetFilters = () => {
    // Reset to Arts category instead of empty
    const artsCategory = categories.find(cat => cat.name === 'Arts');
    if (artsCategory) {
      setCategoryId(artsCategory.id.toString());
      setMiddleCategoryId('');
      onFilterChange({ categoryId: artsCategory.id });
    }
  };

  return (
    <div className="bg-white p-4 mb-4 rounded-lg shadow">
      <div className="flex flex-col space-y-4">
        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
            Filter by Category
          </label>
          <select
            id="category"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
            disabled={isLoading}
          >
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        {/* Middle Category Dropdown */}
        <div>
          <label htmlFor="middleCategory" className="block text-sm font-medium text-gray-700 mb-1">
            Filter by Middle Category
          </label>
          <select
            id="middleCategory"
            value={middleCategoryId}
            onChange={(e) => setMiddleCategoryId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
            disabled={isLoadingMiddleCategories || !categoryId || middleCategories.length === 0}
          >
            <option value="">All Middle Categories</option>
            {middleCategories.map((middleCategory) => (
              <option key={middleCategory.id} value={middleCategory.id}>
                {middleCategory.name}
              </option>
            ))}
          </select>
          {isLoadingMiddleCategories && (
            <p className="mt-1 text-sm text-gray-500">Loading middle categories...</p>
          )}
          {!categoryId && (
            <p className="mt-1 text-sm text-gray-500">Please select a category first</p>
          )}
        </div>
        
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleApplyFilters}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 min-h-[44px] flex-1 md:flex-none"
          >
            Apply Filters
          </button>
          <button
            onClick={handleResetFilters}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 min-h-[44px] flex-1 md:flex-none"
          >
            Reset to Arts
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubcategoryFilter; 